/*
Copyright 2024 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/
import Cookies from 'js-cookie';
import { getAepCookieName } from '../helpers';
import { logger } from '../logger';

/**
 * @see https://github.com/adobe/alloy/blob/main/src/components/Identity/createDecodeKndctrCookie.js
 */

const ECID_FIELD_NUMBER = 1;

/**
 * Decodes a varint from a buffer starting at the given offset.
 *
 * Variable-width integers, or varints, are at the core of the wire format. They
 * allow encoding unsigned 64-bit integers using anywhere between one and ten
 * bytes, with small values using fewer bytes.
 *
 * Each byte in the varint has a continuation bit that indicates if the byte
 * that follows it is part of the varint. This is the most significant bit (MSB)
 * of the byte (sometimes also called the sign bit). The lower 7 bits are a
 * payload; the resulting integer is built by appending together the 7-bit
 * payloads of its constituent bytes.
 *
 * 10010110 00000001        // Original inputs.
 *  0010110  0000001        // Drop continuation bits.
 *  0000001  0010110        // Convert to big-endian.
 *    00000010010110        // Concatenate.
 *  128 + 16 + 4 + 2 = 150  // Interpret as an unsigned 64-bit integer.
 *
 * @example decodeVarint(new Uint8Array([0b0, 0b1]), 0) // { value: 1, length: 2 }
 * @example decodeVarint(new Uint8Array([0b10010110, 0b00000001], 0) // { value: 150, length: 2 })
 * @param {Uint8Array} buffer
 * @param {number} offset
 * @returns {{ value: number, length: number }} The value of the varint and the
 * number of bytes it takes up.
 */
export const decodeVarint = (buffer: Uint8Array, offset: number) => {
  let value = 0;
  let length = 0;
  let byte;
  do {
    if (offset < 0 || offset + length >= buffer.length) {
      throw new Error('Invalid varint: buffer ended unexpectedly');
    }
    byte = buffer[offset + length];
    // Drop the continuation bit (the most significant bit), convert it from
    // little endian to big endian, and add it to the accumulator `value`.
    value |= (byte & 0b01111111) << (7 * length);
    // Increase the length of the varint by one byte.
    length += 1;
    // A varint can be at most 10 bytes long for a 64-bit integer.
    if (length > 10) {
      throw new Error('Invalid varint: too long');
    }
  } while (byte & 0b10000000);
  return { value, length };
};

/**
 * | ID | Name   | Used for                                                 |
 * |----|--------|----------------------------------------------------------|
 * | 0  | varint | int32, int64, uint32, uint64, sint32, sint64, bool, enum |
 * | 1  | I64    | fixed64, sfixed64, double                                |
 * | 2  | LEN    | string, bytes                                            |
 * | 3  | SGROUP | group start (deprecated)                                 |
 * | 4  | EGROUP | group end (deprecated)                                   |
 * | 5  | I32    | fixed32, sfixed32, float                                 |
 */
const WIRE_TYPES = Object.freeze({
  VARINT: 0,
  I64: 1,
  LEN: 2,
  SGROUP: 3,
  EGROUP: 4,
  I32: 5,
});

/**
 * Given a protobuf as a Uint8Array and based on the protobuf definition for the
 * kndctr cookie provided at https://git.corp.adobe.com/pages/experience-edge/konductor/#/api/identifying-visitors?id=device-identifiers,
 * this function should return the ECID as a string.
 * The decoding of the protobuf is hand-crafted in order to save on size
 * compared to the full protobuf.js library.
 * @param {Uint8Array} buffer
 * @returns {string}
 */
const decodeKndctrProtobuf = (buffer: Uint8Array) => {
  let offset = 0;
  let ecid = null;
  while (offset < buffer.length && !ecid) {
    // A protobuf message is a series of records. Each record is a tag, the length,
    // and the value.
    // A record always starts with the tag. The “tag” of a record is encoded as
    // a varint formed from the field number and the wire type via the formula
    // `(field_number << 3) | wire_type`. In other words, after decoding the
    // varint representing a field, the low 3 bits tell us the wire type, and the rest of the integer tells us the field number.
    // So the first step is to decode the varint
    const { value: tag, length: tagLength } = decodeVarint(buffer, offset);
    offset += tagLength;
    // Next, we get the wire type and the field number.
    // You take the last three bits to get the wire type and then right-shift by
    // three to get the field number.
    const wireType = tag & 0b111;
    const fieldNumber = tag >> 3;
    // We only care about the ECID field, so we will skip any other fields until
    // we find it.
    if (fieldNumber === ECID_FIELD_NUMBER) {
      // The wire type for the ECID field is 2, which means it is a length-delimited field.
      if (wireType === WIRE_TYPES.LEN) {
        // The next varint will tell us the length of the ECID.
        const fieldValueLength = decodeVarint(buffer, offset);
        offset += fieldValueLength.length;
        // The ECID is a UTF-8 encoded string, so we will decode it as such.
        ecid = new TextDecoder().decode(buffer.slice(offset, offset + fieldValueLength.value));
        offset += fieldValueLength.value;
        return ecid;
      }
    } else {
      // If we don't care about the field, we skip it.
      // The wire type tells us how to skip the field.
      switch (wireType) {
        case WIRE_TYPES.VARINT:
          // Skip the varint
          offset += decodeVarint(buffer, offset).length;
          break;
        case WIRE_TYPES.I64:
          // Skip the 64-bit integer
          offset += 8;
          break;
        case WIRE_TYPES.LEN: {
          // Find the value that represents the length of the vield
          const fieldValueLength = decodeVarint(buffer, offset);
          offset += fieldValueLength.length + fieldValueLength.value;
          break;
        }
        case WIRE_TYPES.SGROUP:
          // Skip the start group
          break;
        case WIRE_TYPES.EGROUP:
          // Skip the end group
          break;
        case WIRE_TYPES.I32:
          // Skip the 32-bit integer
          offset += 4;
          break;
        default:
          throw new Error(`Malformed kndctr cookie. Unknown wire type: ${wireType}`);
      }
    }
  }

  // No ECID was found. Maybe the cookie is malformed, maybe the format was changed.
  throw new Error('No ECID found in cookie.');
};

/**
 * takes a base64 string of bytes and returns a Uint8Array
 * @param {string} base64
 * @returns {Uint8Array}
 */
const base64ToBytes = (base64: string) => {
  const binString = atob(base64);
  return Uint8Array.from(binString, m => m.codePointAt(0)!);
};

export function decodeKndctrCookie() {
  const { log } = logger({})('decodeKndctrCookie');
  const kndctrCookieName = getAepCookieName('identity');
  const cookie = Cookies.get(kndctrCookieName);
  if (!cookie) {
    return null;
  }
  try {
    const decodedCookie = decodeURIComponent(cookie).replace(/_/g, '/').replace(/-/g, '+');
    // cookie is a base64 encoded byte representation of a Identity protobuf message
    // and we need to get it to a Uint8Array in order to decode it

    const cookieBytes = base64ToBytes(decodedCookie);
    return decodeKndctrProtobuf(cookieBytes);
  } catch (error) {
    log.warn(`Unable to decode ECID from ${kndctrCookieName} cookie`, error);
    return null;
  }
}

/**
 *
 * @returns boolean
 * @description this function will look for ont trust cookie
 * if its available then in the value if groups query contains 6:1 then it will treturn true
 * otherwise false
 */
export const checkOneTrustCookie = () => {
  try {
    const cookie = Cookies.get('OptanonConsent');
    if (!cookie) {
      return false;
    }
    const decodedValue = decodeURIComponent(cookie);
    const groupsValue = new URLSearchParams(decodedValue)?.get('groups');
    return groupsValue?.includes('6:1') ?? false;
  } catch (e) {
    return false;
  }
};
